import { NextSeo, WebPageJsonLd } from "next-seo";
import { AggregateGrid } from "~/components/AggregateDisplay";
import { Navbar } from "~/components/Navbar/component";
import { PageHeader } from "~/components/PageHeader";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { ASSET_CLASS_IDS } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { getHomePageSeo } from "~/modules/app/seo/getHomePageSeo";
import { getTickerMarqueeData } from "~/modules/asset_classes/api/getTickerMarqueeData";
import {
    TimeSeriesItem,
    TimeSeriesPoint,
    TimeSeriesUnits,
} from "~/modules/asset_timeseries_v4/types";
import { queryAssets } from "~/modules/assets/api/router";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { AssetTicker } from "~/modules/assets/components/AssetTicker";
import type { TickerGroup } from "~/modules/assets/components/AssetTicker";
import { AssetColumns } from "~/modules/assets/types";
import { AssetScreenerListingPage } from "~/modules/asset_screener/pages/listing/AssetScreenerListingPage";
import {
    AssetScreenerPageProps,
    getAssetScreenerStaticProps,
} from "~/modules/asset_screener/pages/listing/getAssetScreenerPageProps";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { LatestAssetsCard } from "~/modules/main/pages/home/LatestAssetsCard";
import { getLatestAssetRows } from "~/modules/main/pages/home/LatestAssetsCard/getLatestAssetRows";
import type { LatestAssetRow } from "~/modules/main/pages/home/LatestAssetsCard/types";
import { TotalRwaValueChart } from "~/modules/main/pages/home/component";
import {
    HIGH_YIELD_ASSET_CLASS_IDS,
    TREASURY_YIELD_QUERY_START_DATE,
} from "~/modules/main/pages/home/consts";
import { HomeAssetPerformance } from "~/modules/main/pages/home/home-asset-performance";
import { HomeHighYieldChart } from "~/modules/main/pages/home/HomeHighYieldChart";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { HomeNetFlowsCard } from "~/modules/main/pages/home/HomeNetFlowsCard";
import { HomeTreasuryYieldChart } from "~/modules/main/pages/home/HomeTreasuryYieldChart";
import {
    getOverviewSummaryData,
    type OverviewSummaryData,
} from "~/modules/main/pages/home/getOverviewSummaryData";
import { FilterOperators, SortDirections } from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import type { NetFlowsItemLabels } from "~/modules/visualization/components/NetFlowsChart/component";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import { Routes } from "~/routes";
import { RWA_COLORS } from "~/styles/colors";
import { queryDataDb } from "~/server/datadb";
import { interpolateMissingDates } from "~/modules/visualization/util/interpolateMissingDates";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

interface SofrRow {
    date: string;
    measure_value: number;
}

const EXCLUDE_STABLECOINS_FILTERS = [
    {
        field: TokenTimeseriesColumns.assetClassID,
        operator: FilterOperators.NotEquals,
        value: ASSET_CLASS_IDS.Stablecoin,
    },
];
const NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH = 30;
const MIN_CREDIT_YIELD_CIRCULATING_VALUE = 10_000_000;
// Excluded after manual QA for implausible, zero-heavy, or stale APY data.
const CREDIT_YIELD_EXCLUDED_ASSET_TICKERS = [
    "ACRED",
    "BR0OVSCTF004",
    "BR0OVSCTF012",
    "BRVERTDBS0I1",
    "GSTR",
    "mGLO",
    "mGLOBAL",
    "pEAK",
    "RLP",
    "STAC",
    "USD3",
    "xSOLY",
];
// `dbt.stg_dl_reference_rates` holds several New York Fed reference rates, so the
// SOFR series has to be filtered down to its own measure.
const SOFR_MEASURE_ID = 89;
// Negative so it cannot collide with an asset group ID (the average series uses -1).
const SOFR_GROUP_ID = -2;

function truncateNetFlowAssetManagerLabel(name: string): string {
    if (name.length <= NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH) {
        return name;
    }

    return `${name
        .slice(0, NET_FLOW_ASSET_MANAGER_LABEL_MAX_LENGTH - 1)
        .trimEnd()}…`;
}

async function getSofrSeries(): Promise<TimeSeriesItem[]> {
    try {
        const rows: SofrRow[] = await queryDataDb({
            sql: `
                SELECT date, measure_value
                FROM dbt.stg_dl_reference_rates
                WHERE measure_id = ${SOFR_MEASURE_ID}
                    AND date >= '${TREASURY_YIELD_QUERY_START_DATE}'
                ORDER BY date ASC;
            `,
        });

        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const sofrSeries: TimeSeriesItem = {
            measure: {
                id: SOFR_MEASURE_ID,
                unit: TimeSeriesUnits.percent,
                slug: "sofr",
                name: "SOFR",
            },
            points: rows.map((row): TimeSeriesPoint => [
                new Date(row.date).toLocaleDateString("en-CA", {
                    timeZone: "UTC",
                }),
                row.measure_value,
            ]),
            group: {
                id: SOFR_GROUP_ID,
                name: "SOFR",
                slug: "sofr",
                color: RWA_COLORS.slate[500],
                type: "reference_rate",
            },
        };

        return [interpolateMissingDates(sofrSeries)];
    } catch (error) {
        console.error("getSofrSeries failed", error);
        return [];
    }
}

interface HomePageProps
    extends AssetScreenerPageProps, Omit<OverviewSummaryData, "gigatableRows"> {
    latestAssets: { rows: LatestAssetRow[]; total: number };
    tickerGroups: TickerGroup[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    netFlowsByAssetClass: NetFlowsData;
    netFlowsByAsset: NetFlowsData;
    netFlowsByManager: NetFlowsData;
    netFlowAssetLabels: NetFlowsItemLabels;
    creditYieldAssetIDs: number[];
    sofrSeries: TimeSeriesItem[];
}

export async function getStaticProps() {
    const [
        assetScreenerPage,
        overviewSummary,
        latestAssets,
        tickerGroups,
        leagueTableTabs,
        netFlowsByAssetClass,
        netFlowsByAsset,
        netFlowsByManager,
        creditYieldAssets,
        sofrSeries,
    ] = await Promise.all([
        getAssetScreenerStaticProps(),
        getOverviewSummaryData(),
        // NOTE - the LatestAssetsCard uses the New Asset Monitor's filter so the
        // card and the monitor it links to always show the same assets
        getLatestAssetRows(),
        getTickerMarqueeData(),
        queryLeagueTablesByDistributionType({
            name: "home-page",
            filter: {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.NotEquals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
            groups: [
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.assetClasses,
            ],
        }),
        queryNetFlows({
            name: "home-by-asset-class-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.assetClass,
            filters: EXCLUDE_STABLECOINS_FILTERS,
        }),
        queryNetFlows({
            name: "home-by-asset-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.asset,
            filters: EXCLUDE_STABLECOINS_FILTERS,
        }),
        queryNetFlows({
            name: "home-by-manager-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.manager,
            filters: [
                ...EXCLUDE_STABLECOINS_FILTERS,
                {
                    field: TokenTimeseriesColumns.managerID,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
            ],
        }),
        queryAssets({
            query: {
                ...DEFAULT_ASSET_LIST_QUERY,
                name: "home-credit-yield-assets-over-10m",
                fields: ["id", "ticker"],
                filter: {
                    operator: FilterOperators.And,
                    filters: [
                        {
                            operator: FilterOperators.Or,
                            filters: HIGH_YIELD_ASSET_CLASS_IDS.map(
                                (assetClassID) => ({
                                    field: AssetColumns.assetClassID,
                                    operator: FilterOperators.Equals,
                                    value: assetClassID,
                                }),
                            ),
                        },
                        {
                            field: AssetColumns.circulatingAssetValueDollar,
                            operator: FilterOperators.GreaterThanOrEqual,
                            value: MIN_CREDIT_YIELD_CIRCULATING_VALUE,
                        },
                    ],
                },
                sort: {
                    field: AssetColumns.circulatingAssetValueDollar,
                    direction: SortDirections.desc,
                },
                pagination: {
                    page: 1,
                    perPage: 3500,
                },
            },
        }),
        getSofrSeries(),
    ]);

    const netFlowAssetIds = Array.from(
        new Set(
            Object.values(netFlowsByAsset)
                .flat()
                .map((item) => item.group.id),
        ),
    );
    const netFlowAssets = await (netFlowAssetIds.length > 0
        ? queryAssets({
              query: {
                  ...DEFAULT_ASSET_LIST_QUERY,
                  name: "home-net-flow-asset-labels",
                  filter: {
                      operator: FilterOperators.Or,
                      filters: netFlowAssetIds.map((assetId) => ({
                          field: AssetColumns.id,
                          operator: FilterOperators.Equals,
                          value: assetId,
                      })),
                  },
                  pagination: {
                      page: 1,
                      perPage: netFlowAssetIds.length,
                  },
              },
          })
        : { results: [] });
    const netFlowAssetLabels: NetFlowsItemLabels = Object.fromEntries(
        netFlowAssets.results.map((asset) => {
            const assetManager =
                asset.tokens.find((token) => token.asset_manager)
                    ?.asset_manager ??
                asset.manager ??
                asset.issuer;

            return [
                asset.id,
                {
                    primary: asset.ticker || asset.name,
                    secondary: assetManager?.name
                        ? truncateNetFlowAssetManagerLabel(assetManager.name)
                        : "—",
                },
            ];
        }),
    );

    return {
        ...assetScreenerPage,
        props: {
            ...assetScreenerPage.props,
            // NOTE - only pass `aggregates` through - the full gigatableRows
            // set is not rendered on the homepage and would bloat the ISR payload
            aggregates: overviewSummary.aggregates,
            latestAssets,
            tickerGroups,
            leagueTableTabs,
            netFlowsByAssetClass,
            netFlowsByAsset,
            netFlowsByManager,
            netFlowAssetLabels,
            creditYieldAssetIDs: creditYieldAssets.results
                .filter(
                    (asset) =>
                        asset.ticker === null ||
                        !CREDIT_YIELD_EXCLUDED_ASSET_TICKERS.includes(
                            asset.ticker,
                        ),
                )
                .map((asset) => asset.id),
            sofrSeries,
        },
    };
}

export default function Home(props: HomePageProps) {
    const {
        aggregates,
        assets,
        latestAssets,
        tickerGroups,
        leagueTableTabs,
        netFlowsByAssetClass,
        netFlowsByAsset,
        netFlowsByManager,
        netFlowAssetLabels,
        creditYieldAssetIDs,
        sofrSeries,
    } = props;

    return (
        <PageTrack route={Routes.home}>
            <NextSeo
                canonical={addBaseUrl(Routes.home)}
                title="Analytics on Tokenized Real-World Assets"
            />
            <BreadcrumbSEO items={[]} />
            <WebPageJsonLd {...getHomePageSeo({ assetClasses: [] })} />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.home} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.home}>
                <AssetTicker groups={tickerGroups} />
                <PageWrapper>
                    <PageHeader
                        title="Global Market Overview"
                        subtitle={
                            <p>
                                Welcome to RWA.xyz. Discover every tokenized
                                real-world asset and the broader tokenization
                                ecosystem, from asset managers to tokenization
                                platforms to blockchains.{" "}
                                <span className="text-muted-foreground">
                                    Email team@rwa.xyz for questions or help.
                                </span>
                            </p>
                        }
                    />

                    <AggregateGrid aggregates={aggregates} />

                    <VisualizationGrid className="my-6 w-full">
                        <TotalRwaValueChart />

                        <HomeAssetPerformance />

                        <HomePageLeagueTable
                            leagueTableTabs={leagueTableTabs}
                            analyticsTableName="homepage-league-table"
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />

                        <LeagueTablePieChart
                            title="Total RWA Value"
                            leagueTableTabs={leagueTableTabs}
                            defaultGroupBy={LeagueTableGroupTypes.platforms}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />

                        <HomeTreasuryYieldChart sofrSeries={sofrSeries} />

                        <HomeHighYieldChart
                            assets={assets}
                            creditYieldAssetIDs={creditYieldAssetIDs}
                        />
                        <HomeNetFlowsCard
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                            netFlowsByAssetClass={netFlowsByAssetClass}
                            netFlowsByAsset={netFlowsByAsset}
                            netFlowsByManager={netFlowsByManager}
                            netFlowAssetLabels={netFlowAssetLabels}
                        />
                        <LatestAssetsCard
                            assets={latestAssets.rows}
                            total={latestAssets.total}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            }}
                        />
                    </VisualizationGrid>

                    <AssetScreenerListingPage assets={assets} variant="home" />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
